import './OkulSiparisPage.scss'

import { CaretDownFilled, CaretDownOutlined, SearchOutlined } from '@ant-design/icons'
import { Button, Col, DatePicker, Input, Row, Select, Table } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import { saveAs } from 'file-saver'
import { RangeValue } from 'rc-picker/lib/interface'
import React, { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import * as XLSX from 'xlsx'

import AppContext from '../../../../context/AppContext'
import { useOkulSiparisleriQuery } from '../../../../gql/GeneratedGraphqlTypes'
import { getFormattedPrice } from '../../../../helpers/Formatting'
import Spinner from '../../../spinner/Spinner'
import { ReactComponent as AktarIcon } from './icons/aktar.svg'
import { ReactComponent as ConfirmIcon } from './icons/confirm.svg'
import { ReactComponent as HepsiJet } from './icons/hepsiJet.svg'
import { ReactComponent as NotConfirmIcon } from './icons/notConfirm.svg'
import { ReactComponent as WaitingIcon } from './icons/waiting.svg'
import { ReactComponent as YurtIci } from './icons/yurtKargo.svg'

dayjs.extend(isBetween)

const { RangePicker } = DatePicker
const { Option } = Select
const { Search } = Input

const OkulSiparisPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [searchValue, setSearchValue] = useState<string>('')
  const [selectedDurum, setSelectedDurum] = useState<string | undefined>(undefined)
  const [selectedOdeme, setSelectedOdeme] = useState<string | undefined>(undefined)
  const [selectedKargo, setSelectedKargo] = useState<string | undefined>(undefined)
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(undefined)
  const { okul, siparisYetki } = useContext(AppContext)
  const acilis = dayjs(okul?.okul_acilis_tarih ?? '2024-06-01')
  const bitis = dayjs()
  const [dateRange, setDateRange] = useState<RangeValue<Dayjs>>(
      acilis.isBefore(bitis) ? [acilis, bitis] : [bitis, acilis]
  )
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  const navigate = useNavigate()
  const { data, loading, error, refetch } = useOkulSiparisleriQuery({
    variables: {
      sube_id: okul?.id,
      bas_tarih: dateRange?.[0]?.format('YYYY-MM-DD') ?? '2024-06-01',
      bit_tarih: dateRange?.[1]?.format('YYYY-MM-DD') ?? '2025-06-01',
      siparis_durumu: selectedDurum ?? null,
      odeme_tipi: selectedOdeme ?? null,
      kategori_secimi: parseInt(selectedCategory ? selectedCategory : '0') ?? null,
      kargo_firmasi: selectedKargo ?? null,
      arama: searchValue || null, // 2 karakterden küçükse arama yapılmaz
    },
    fetchPolicy: 'network-only',
  })
  const handleSearch = () => {
    setSearchValue(searchTerm)
  }

  // Filtreleme değiştikçe veriyi tekrar çek
  useEffect(() => {
    refetch()
  }, [searchValue, selectedDurum, selectedOdeme, selectedKargo, dateRange, refetch])

  if (loading) {
    return <Spinner showSpinner={loading} color="red" />
  }

  if (error) {
    console.error(error)
    navigate('/')
    return null
  }

  const handleFilterChange = (filterType: 'durum' | 'odeme' | 'kargo' | 'kategori', value: string | undefined) => {
    if (value === null) {
      if (filterType === 'durum') setSelectedDurum(value)
      if (filterType === 'odeme') setSelectedOdeme(value)
      if (filterType === 'kargo') setSelectedKargo(value)
      if (filterType === 'kategori') setSelectedCategory(value)
    } else {
      if (filterType === 'durum') setSelectedDurum(value)
      if (filterType === 'odeme') setSelectedOdeme(value)
      if (filterType === 'kargo') setSelectedKargo(value)
      if (filterType === 'kategori') setSelectedCategory(value)
    }
  }

  const handleDateChange = (values: RangeValue<Dayjs>) => {
    setDateRange(values)

    if (values && values[0] && values[1]) {
      refetch({
        sube_id: okul?.id,
        bas_tarih: values[0].format('YYYY-MM-DD'),
        bit_tarih: values[1].format('YYYY-MM-DD'),
        siparis_durumu: selectedDurum ?? null,
        odeme_tipi: selectedOdeme ?? null,
        kategori_secimi: parseInt(selectedCategory ? selectedCategory : '0') ?? null,
        kargo_firmasi: selectedKargo ?? null,
        arama: searchValue || null,
      })
    }
  }

  const filteredData = data?.okulSiparisleri?.siparisler?.filter(Boolean) ?? []
  const totalItems = filteredData.length
  const displayedItems = selectedRowKeys.length
  const siparisDurumOptions = [
    <Option key="tum" value={null}>
      Tümü
    </Option>,
    ...(data?.okulSiparisleri?.siparis_durumlari?.map((durum) => (
      <Option key={durum?.key} value={durum?.key}>
        {durum?.value}
      </Option>
    )) || []),
  ]

  const odemeTipiOptions = [
    <Option key="tum" value={null}>
      Tümü
    </Option>, // 🔹 Tümünü ekledik
    ...(data?.okulSiparisleri?.odeme_tipleri?.map((odeme) => (
      <Option key={odeme?.key} value={odeme?.key}>
        {odeme?.value}
      </Option>
    )) || []),
  ]



  const kategoriOptions = [
    <Option key="tum" value={null}>
      Tümü
    </Option>, // 🔹 Tümünü ekledik
    ...(data?.okulSiparisleri?.uyeKategorileri?.map((kargo) => (
      <Option key={kargo?.id} value={kargo?.id}>
        {kargo?.isim}
      </Option>
    )) || []),
  ]

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      sorter: (a: any, b: any) => a.id - b.id,
      defaultSortOrder: 'descend' as 'descend' | 'ascend' | undefined,
    },
    { title: 'Sınıf', dataIndex: 'sinif_ismi', key: 'sinif_ismi' },
    { title: 'Müşteri İsmi', dataIndex: 'musteri_ismi', key: 'musteri_ismi' },
    { title: 'Öğrenci İsmi', dataIndex: 'ogrenci_ismi', key: 'ogrenci_ismi' },
    {
      title: 'Toplam Tutar',
      dataIndex: 'toplam_tutar',
      key: 'toplam_tutar',
      sorter: (a: any, b: any) => parseFloat(a.toplam_tutar) - parseFloat(b.toplam_tutar),
      render: (value: string) => getFormattedPrice(value),
    },
    { title: 'Ödeme Tipi', dataIndex: 'odeme_tipi_aciklama', key: 'odeme_tipi_aciklama' },
    {
      title: 'Kargo Firması',
      dataIndex: 'kargo_firmasi',
      key: 'kargo_firmasi',
      render: (_: any, record: any) => (
        <>
          {record.kargo_firmasi === 'HepsiJet' ? (
            <HepsiJet />
          ) : record.kargo_firmasi === 'Yurt İçi Kargo' ? (
            <YurtIci />
          ) : (
            record.kargo_firmasi
          )}
        </>
      ),
    },
    {
      title: 'Sipariş Durumu',
      dataIndex: 'siparis_durumu_aciklama',
      key: 'siparis_durumu_aciklama',
      render: (_: any, record: any) => (
        <>
          {record.siparis_durumu === 'B' ? (
            <div className="order-status-table">
              <WaitingIcon /> {record.siparis_durumu_aciklama}{' '}
            </div>
          ) : record.siparis_durumu === 'T' ? (
            <div className="order-status-table">
              <ConfirmIcon />
              {record.siparis_durumu_aciklama}
            </div>
          ) : record.siparis_durumu === 'I' ? (
            <div className="order-status-table">
              <NotConfirmIcon />
              {record.siparis_durumu_aciklama}
            </div>
          ) : (
            <> {record.siparis_durumu_aciklama} </>
          )}
        </>
      ),
    },
    {
      title: 'Tarih',
      dataIndex: 'tarih',
      key: 'tarih',
      sorter: (a: any, b: any) => dayjs(a.tarih).valueOf() - dayjs(b.tarih).valueOf(),
    },

    {
      title: '',
      dataIndex: 'siparis_detay',
      key: 'siparis_detay',
      render: (_: any, record: any) => (
        <Button onClick={() => navigate(`${record.id}`)} className="okul-yonetim-admin-detail-button">
          Detay
        </Button>
      ),
    },
  ]

  const exportAllToExcel = () => {
    const worksheetData: any[] = []

    const headers = [
      'Sipariş id',
      'Sınıf',
      'Üye',
      'Mail',
      'Öğrenci Adı',
      'Tarih',
      'Durum',
      'Ödeme tipi',
      'Kargo tipi',
      'Toplam adet',
      'Tutar',
      'aciklama',
    ]
    worksheetData.push(headers)

    data?.okulSiparisleri?.siparisler.forEach((siparisler: any) => {
      const transformedData = [
        siparisler.id || '',
        siparisler.sinif_ismi || '',
        siparisler.musteri_ismi || '',
        siparisler.email || '',
        siparisler.ogrenci_ismi || '',
        siparisler.tarih || '',
        siparisler.siparis_durumu_aciklama || '',
        siparisler.odeme_tipi_aciklama || '',
        siparisler.kargo_tipi_aciklama || '',
        siparisler.adet_sayisi || 0,
        siparisler.toplam_tutar || '',
        siparisler.kargo_notu || '',
      ]
      worksheetData.push(transformedData)
    })

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData)

    const colWidths = worksheetData[0].map((_: string, colIndex: number) =>
      Math.max(...worksheetData.map((row) => (row[colIndex] ? row[colIndex].toString().length : 10))),
    )
    worksheet['!cols'] = colWidths.map((width: number) => ({ wch: width }))

    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'TümVeriler')
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
    const today = new Date().toISOString().split('T')[0]
    const fileName = `OkulSiparis-${today}.xlsx`

    saveAs(new Blob([excelBuffer]), fileName)
  }

  return (
    <div className="okul-siparis-page">
      <div className="okul-sepeti-admin-header">
        <h1>Okul Siparişleri</h1>
          <Button onClick={exportAllToExcel} className="export-button">
            <AktarIcon/> Dışa Aktar{' '}
          </Button>
      </div>
      {siparisYetki ? (
        <>
          <Row gutter={16} className="filter-section">
            <Col lg={12} xl={6} md={6} sm={12} xs={24}>
              <span className="okul-order-page-filter-title">Müşteri, Sınıf veya Sipariş Ara </span>
              <Search
                placeholder="Müşteri, Sınıf veya Sipariş Ara"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onSearch={handleSearch}
                allowClear
                enterButton={
                  <div className="search-suffix-div">
                    Ara <SearchOutlined />{' '}
                  </div>
                }
              />
            </Col>
            <Col lg={12} xl={4} md={6} sm={12} xs={24}>
              <span className="okul-order-page-filter-title">Sipariş Durumu</span>
              <Select
                suffixIcon={<CaretDownFilled style={{ fontSize: 20 }} />}
                placeholder="Seçiniz"
                value={selectedDurum}
                onChange={(value) => handleFilterChange('durum', value)}
                allowClear
              >
                {siparisDurumOptions}
              </Select>
            </Col>
            <Col lg={12} xl={4} md={6} sm={12} xs={24}>
              <span className="okul-order-page-filter-title">Kategori</span>
              <Select
                suffixIcon={<CaretDownFilled style={{ fontSize: 20 }} />}
                placeholder="Seçiniz"
                value={selectedCategory}
                onChange={(value) => handleFilterChange('kategori', value)}
                allowClear
              >
                {kategoriOptions}
              </Select>
            </Col>
            <Col lg={12} xl={4} md={6} sm={12} xs={24}>
              <span className="okul-order-page-filter-title">Ödeme Tipi</span>

              <Select
                placeholder="Seçiniz"
                suffixIcon={<CaretDownFilled style={{ fontSize: 20 }} />}
                value={selectedOdeme}
                onChange={(value) => handleFilterChange('odeme', value)}
                allowClear
              >
                {odemeTipiOptions}
              </Select>
            </Col>
            <Col lg={12} xl={6} md={6} sm={12} xs={24}>
              <span className="okul-order-page-filter-title">Tarih Aralığı </span>
              <RangePicker
                  allowClear
                suffixIcon={
                  <>
                    Değiştir <CaretDownOutlined />{' '}
                  </>
                }
                value={dateRange}
                onChange={handleDateChange}
                format="YYYY-MM-DD"
              />
            </Col>
          </Row>

          <Table
            dataSource={filteredData as object[]}
            columns={columns}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              pageSizeOptions: ['10', '20', '50', '100'],
              total: totalItems,
              showTotal: (total) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <span style={{ marginLeft: '10px' }}>
                    {' '}
                    {/* Buton ile metin arasına boşluk koyuyoruz */}
                    Seçilen: {displayedItems} / Toplam: {total}
                  </span>
                </div>
              ),
              onChange: (page, size) => {
                setCurrentPage(page)
                setPageSize(size)
              },
            }}
            loading={loading}
            rowKey="id"
            className="okul-yonetim-admin-table"
            rowClassName={(record, index) => (index % 2 === 0 ? 'table-row-light' : 'table-row-dark')}
            rowSelection={{
              type: 'checkbox',
              ...Selection,
              onChange: (keys) => setSelectedRowKeys(keys as string[]),
            }}
          />
        </>
      ) : (
        <p>Siparişleri görme yetkiniz bulunmamaktadır. İlgili okul temsilcinizden talep edebilirsiniz.</p>
      )}
    </div>
  )
}

export default OkulSiparisPage
